import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import FlowchartViewer from './FlowchartViewer';
import axiosInstance from '../api/axios';
import { API_ENDPOINTS } from '../api/endpoints';
import { TENANT_PATHS } from '../routing/paths.ts';
import { useProcessBackNavigation } from '../hooks/useProcessBackNavigation.ts';
import { useAuditYear } from '../contexts/AuditYearContext.tsx';

// Mock flowchart data for different processes (will be replaced with backend API call)
const mockFlowchartData = {
  'otc-p1': {
    nodes: [
      { id: '1', nodeType: 'start', nodeDescription: 'Start', actor: '', system: '', soxControl: '', document: '' },
      { id: '2', nodeType: 'process', nodeDescription: 'Data sync initiated', actor: 'Chargebee System', system: 'Chargebee', soxControl: 'OTC-01', document: 'Sync Log', keySystem: true },
      { id: '3', nodeType: 'process', nodeDescription: 'Revenue data extracted', actor: 'Finance Ops', system: 'Chargebee', soxControl: 'OTC-02', document: 'Revenue Report', keyControl: true },
      { id: '4', nodeType: 'decision', nodeDescription: 'Data validation check', actor: 'Finance Ops', system: '', soxControl: '', document: '' },
      { id: '5', nodeType: 'process', nodeDescription: 'Data imported to NetSuite', actor: 'Finance Ops', system: 'NetSuite', soxControl: 'OTC-03', document: 'Import Log', keySystem: true },
      { id: '6', nodeType: 'process', nodeDescription: 'Reconciliation performed', actor: 'Accounting Team', system: 'NetSuite', soxControl: 'OTC-04', document: 'Recon Report', keyControl: true },
      { id: '7', nodeType: 'end', nodeDescription: 'End', actor: '', system: '', soxControl: '', document: '' }
    ],
    connectors: [
      { source: '1', target: '2', label: '' },
      { source: '2', target: '3', label: '' },
      { source: '3', target: '4', label: '' },
      { source: '4', target: '5', label: 'Yes' },
      { source: '4', target: '3', label: 'No' },
      { source: '5', target: '6', label: '' },
      { source: '6', target: '7', label: '' }
    ]
  },
  'otc-p2': {
    nodes: [
      { id: '1', nodeType: 'start', nodeDescription: 'Start', actor: '', system: '', soxControl: '', document: '' },
      { id: '2', nodeType: 'process', nodeDescription: 'Review monthly revenue', actor: 'Revenue Team', system: 'NetSuite', soxControl: 'OTC-05', document: 'Revenue Schedule', keySystem: true },
      { id: '3', nodeType: 'process', nodeDescription: 'ASC 606 compliance check', actor: 'Revenue Team', system: 'RevPro', soxControl: 'OTC-06', document: 'Compliance Checklist', keyControl: true },
      { id: '4', nodeType: 'decision', nodeDescription: 'Compliance validated?', actor: 'Revenue Team', system: '', soxControl: '', document: '' },
      { id: '5', nodeType: 'process', nodeDescription: 'Revenue recognized', actor: 'Accounting Team', system: 'NetSuite', soxControl: 'OTC-07', document: 'Journal Entry', keySystem: true },
      { id: '6', nodeType: 'end', nodeDescription: 'End', actor: '', system: '', soxControl: '', document: '' }
    ],
    connectors: [
      { source: '1', target: '2', label: '' },
      { source: '2', target: '3', label: '' },
      { source: '3', target: '4', label: '' },
      { source: '4', target: '5', label: 'Yes' },
      { source: '4', target: '3', label: 'No' },
      { source: '5', target: '6', label: '' }
    ]
  },
  // Default flowchart for other processes
  'default': {
    nodes: [
      { id: '1', nodeType: 'start', nodeDescription: 'Start', actor: '', system: '', soxControl: '', document: '' },
      { id: '2', nodeType: 'process', nodeDescription: 'Process step placeholder', actor: 'Team Member', system: 'System', soxControl: 'CTRL-01', document: 'Document', keySystem: false },
      { id: '3', nodeType: 'end', nodeDescription: 'End', actor: '', system: '', soxControl: '', document: '' }
    ],
    connectors: [
      { source: '1', target: '2', label: '' },
      { source: '2', target: '3', label: '' }
    ]
  }
};

function ProcessFlowchartView() {
  const navigate = useNavigate();
  const location = useLocation();
  const { cycleId, processId } = useParams();
  const { selectedAuditYearId } = useAuditYear();
  const shouldSyncInitialPositions = location.state?.syncInitialPositions === true;
  const initialVersionFromNavigation = location.state?.versionNumber;
  const { goBack: navigateBackFromFlowchart } = useProcessBackNavigation(TENANT_PATHS.SOX_DASHBOARD);
  const [flowchartData, setFlowchartData] = useState(null);
  const [processData, setProcessData] = useState(null);
  const [flowchartVersionId, setFlowchartVersionId] = useState(null);
  const [flowchartVersionStatus, setFlowchartVersionStatus] = useState('');
  const [flowchartVersionNumber, setFlowchartVersionNumber] = useState(null);
  const [selectedVersionNumber, setSelectedVersionNumber] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProcessDetails = async () => {
      try {
        const response = await axiosInstance.get(API_ENDPOINTS.getProcessById(processId));
        if (response.data?.success) {
          setProcessData(response.data.data);
        } else {
          setError('Failed to load process details');
        }
      } catch (err) {
        setError('An error occurred while fetching process details');
        console.error(err);
      }
    };

    fetchProcessDetails();
  }, [processId]);

  const fetchFlowchart = useCallback(
    async ({
      versionNumber = null,
      syncSelectedVersion = false,
      showLoader = false,
      allowMockFallback = false,
    } = {}) => {
      try {
        if (showLoader) {
          setLoading(true);
        }

        const params = {};
        if (versionNumber != null) params.versionNumber = versionNumber;
        if (selectedAuditYearId) params.audit_year_id = selectedAuditYearId;
        const response = await axiosInstance.get(
          API_ENDPOINTS.getFlowchart(cycleId, processId),
          Object.keys(params).length ? { params } : {}
        );

        if (!response.data?.success || !response.data?.flowchartData) {
          setError('Failed to load flowchart');
          return false;
        }

        setFlowchartData(response.data.flowchartData);
        setFlowchartVersionId(response.data.flowchartVersionId || null);
        setFlowchartVersionStatus(response.data.flowchartVersionStatus || '');
        setFlowchartVersionNumber(response.data.flowchartVersionNumber || null);
        if (syncSelectedVersion) {
          setSelectedVersionNumber(response.data.versionNumber ?? null);
        }
        setError(null);
        return true;
      } catch (err) {
        console.error('Error fetching flowchart:', err);
        if (allowMockFallback) {
          const mockData = mockFlowchartData[processId] || mockFlowchartData.default;
          setFlowchartData(mockData);
          setFlowchartVersionId(null);
          setFlowchartVersionStatus('');
          setFlowchartVersionNumber(null);
          if (syncSelectedVersion) {
            setSelectedVersionNumber(null);
          }
          setError('Using mock data - API unavailable');
        } else {
          setError('Failed to load selected version');
        }
        return false;
      } finally {
        if (showLoader) {
          setLoading(false);
        }
      }
    },
    [cycleId, processId, selectedAuditYearId]
  );

  useEffect(() => {
    const navVersion =
      initialVersionFromNavigation == null || initialVersionFromNavigation === ''
        ? null
        : Number(initialVersionFromNavigation);
    const loadInitial = async () => {
      let versionToLoad = Number.isFinite(navVersion) ? navVersion : null;
      // When no explicit version is requested, open the LATEST version (the
      // user's in-progress draft if one exists) rather than always the active/
      // published one. The backend writes edits to a draft, so loading the
      // published version on every reload made saved edits appear lost.
      if (versionToLoad == null) {
        try {
          const versionParams = selectedAuditYearId
            ? { audit_year_id: selectedAuditYearId }
            : undefined;
          const vres = await axiosInstance.get(
            API_ENDPOINTS.getFlowchartVersions(cycleId, processId),
            versionParams ? { params: versionParams } : {}
          );
          const versions = Array.isArray(vres?.data?.versions) ? vres.data.versions : [];
          const latest = versions
            .map((v) => Number(v?.versionNumber))
            .filter((n) => Number.isFinite(n))
            .sort((a, b) => b - a)[0];
          if (Number.isFinite(latest)) versionToLoad = latest;
        } catch (e) {
          // Fall back to the default (active) version on any error.
        }
      }
      fetchFlowchart({
        versionNumber: versionToLoad,
        syncSelectedVersion: true,
        showLoader: true,
        allowMockFallback: true,
      });
    };
    loadInitial();
  }, [fetchFlowchart, initialVersionFromNavigation, cycleId, processId, selectedAuditYearId]);

  // Consume one-time navigation sync flag so refresh does not retrigger position sync.
  useEffect(() => {
    if (!shouldSyncInitialPositions) return;
    navigate(location.pathname, {
      replace: true,
      state: {
        ...(location.state || {}),
        syncInitialPositions: false,
      },
    });
  }, [location.pathname, location.state, navigate, shouldSyncInitialPositions]);

  const handleVersionNumberChange = useCallback(
    async (nextVersionNumber) => {
      const previousVersionNumber = selectedVersionNumber;
      if (previousVersionNumber === nextVersionNumber) return true;

      const loaded = await fetchFlowchart({
        versionNumber: nextVersionNumber,
        syncSelectedVersion: true,
      });

      if (!loaded) {
        setSelectedVersionNumber(previousVersionNumber);
        setError(null);
      }

      return loaded;
    },
    [fetchFlowchart, selectedVersionNumber]
  );

  const handleRefreshForReturnedVersion = useCallback(
    async (nextVersionNumber) => {
      return fetchFlowchart({
        versionNumber: nextVersionNumber,
        syncSelectedVersion: true,
      });
    },
    [fetchFlowchart]
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading flowchart...</p>
        </div>
      </div>
    );
  }

  if (error && !flowchartData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={navigateBackFromFlowchart}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <FlowchartViewer
      flowchartData={flowchartData}
      processData={processData}
      onBack={navigateBackFromFlowchart}
      cycleId={cycleId}
      processId={processId}
      flowchartVersionId={flowchartVersionId}
      flowchartVersionStatus={flowchartVersionStatus}
      flowchartVersionNumber={flowchartVersionNumber}
      setFlowchartVersionId={setFlowchartVersionId}
      setFlowchartVersionStatus={setFlowchartVersionStatus}
      setFlowchartVersionNumber={setFlowchartVersionNumber}
      selectedVersionNumber={selectedVersionNumber}
      onVersionNumberChange={handleVersionNumberChange}
      onRefreshForVersionNumber={handleRefreshForReturnedVersion}
      syncInitialPositions={shouldSyncInitialPositions}
    />
  );
}

export default ProcessFlowchartView;
